// src/components/Chat/ContactList.jsx
import React, { useEffect, useState } from "react";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  setDoc,
  updateDoc,
  where,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../../firebase/config";
import { useAuth } from "../../context/AuthContext";
import AddIcon from "@mui/icons-material/Add";
import Avatar from "@mui/material/Avatar";
import "./ContactList.css";

/************************************************************
 * Utility: Normalize phone input → E.164
 ************************************************************/
function normalizePhoneNumber(input, defaultCountry = "+91") {
  if (!input) return "";

  let num = input.replace(/[^0-9+]/g, "");

  if (num.startsWith("+") && num.length > 8) return num;
  if (num.startsWith("00")) return "+" + num.substring(2);
  if (!num.startsWith("+") && num.length === 10) return defaultCountry + num;

  return num.startsWith("+") ? num : defaultCountry + num;
}

export default function ContactList({ onSelectChat, selectedChat }) {
  const { currentUser } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newPhone, setNewPhone] = useState("");

  /************************************************************
   * Load chats of current user
   ************************************************************/
  useEffect(() => {
    if (!currentUser?.uid) return;

    const qChats = query(
      collection(db, "chats"),
      where("users", "array-contains", currentUser.uid)
    );

    const unsub = onSnapshot(qChats, async (snapshot) => {
      const chatList = await Promise.all(
        snapshot.docs.map(async (snapDoc) => {
          const chat = snapDoc.data();
          if (!Array.isArray(chat.users) || chat.users.length < 2) return null;

          const otherUid = chat.users.find((u) => u !== currentUser.uid);
          if (!otherUid) return null;

          const userSnap = await getDoc(doc(db, "users", otherUid));
          const userData = userSnap.exists()
            ? userSnap.data()
            : { name: "Unknown", email: "Unknown" };

          return {
            chatId: snapDoc.id,
            uid: otherUid,
            name: userData.name || userData.email,
            photoURL: userData.photoURL || "",
            lastMessage: chat.lastMessage || "",
            lastMessageTime: chat.lastMessageTime?.toDate?.() || null,
            unreadCount: (chat.unreadCount && chat.unreadCount[currentUser.uid]) || 0,
          };
        })
      );

      const filtered = chatList.filter(Boolean);
      filtered.sort((a, b) => (b.lastMessageTime || 0) - (a.lastMessageTime || 0));
      setContacts(filtered);
      setLoading(false);
    });

    return () => unsub();
  }, [currentUser]);

  /************************************************************
   * Add new contact by phone number
   ************************************************************/
  const handleAddContact = async () => {
    if (!newPhone.trim()) return alert("Enter a phone number");

    const normalized = normalizePhoneNumber(newPhone);

    try {
      const usersRef = collection(db, "users");
      const qUser = query(usersRef, where("phone", "==", normalized));
      const results = await getDocs(qUser);

      if (results.empty) {
        alert("No user exists with this phone number: " + normalized);
        return;
      }

      const otherDoc = results.docs[0];
      const otherUid = otherDoc.id;

      if (otherUid === currentUser.uid) return alert("Cannot add yourself");

      const chatId = [currentUser.uid, otherUid].sort().join("_");
      const chatRef = doc(db, "chats", chatId);

      await setDoc(
        chatRef,
        {
          users: [currentUser.uid, otherUid],
          createdAt: serverTimestamp(),
          lastMessage: "",
          lastMessageTime: null,
          unreadCount: {
            [currentUser.uid]: 0,
            [otherUid]: 0,
          },
        },
        { merge: true }
      );

      setNewPhone("");
    } catch (err) {
      console.error(err);
      alert("Error adding contact");
    }
  };

  /************************************************************
   * Select a chat → reset unread count
   ************************************************************/
  const handleSelect = async (contact) => {
    if (typeof onSelectChat === "function") onSelectChat(contact);

    try {
      const chatRef = doc(db, "chats", contact.chatId);
      await updateDoc(chatRef, {
        [`unreadCount.${currentUser.uid}`]: 0,
      });
    } catch (err) {
      console.error("Failed to reset unread count", err);
    }
  };

  return (
    <div className="contact-list">
      <div className="contact-list-header">
        <h3>Chats</h3>
        <div className="add-contact-box">
          <input
            type="text"
            placeholder="Phone (E.164) e.g. +14155552671"
            value={newPhone}
            onChange={(e) => setNewPhone(e.target.value)}
          />
          <button className="add-btn" onClick={handleAddContact}>
            <AddIcon />
          </button>
        </div>
      </div>

      {loading ? (
        <p className="loading">Loading chats...</p>
      ) : contacts.length === 0 ? (
        <p className="no-chats">No contacts yet. Add someone by phone.</p>
      ) : (
        <div className="contact-items">
          {contacts.map((c) => (
            <div
              key={c.chatId}
              className={`contact-item ${
                selectedChat?.chatId === c.chatId ? "active" : ""
              }`}
              onClick={() => handleSelect(c)}
            >
              <Avatar src={c.photoURL || ""}>
                {c.name ? c.name[0].toUpperCase() : "?"}
              </Avatar>

              <div className="contact-info">
                <div style={{ color: "gold", fontWeight: 600 }}>
                  {c.name || c.email}
                </div>
                {c.lastMessage && <div className="contact-lastmsg">{c.lastMessage}</div>}
              </div>

              {c.unreadCount > 0 && (
                <span className="unread-badge">{c.unreadCount}</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
